import React from 'react';
import { Navigate } from 'react-router-dom';
import { filterXSS } from 'xss';

import DocumentTitle from '@Components/DocumentTitle';
import Container from '@Components/layout/Container';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import Loading from '@Components/LoadingSpinner/LoadingSpinner';
import PageHeader from '@Components/Page/PageHeader';
import PageTitle from '@Components/Page/PageTitle';
import Paper, { PaperDepth, PaperWidth } from '@Components/Paper';
import PaperBody from '@Components/PaperBody';
import { DEFAULT_HOMEPAGE, RouteConfig } from '@Config/routes';
import { useGetStaticPageQuery } from '@Graphql/graphqlTypes.generated';
import { useRouteParams } from '@Hooks/useRouteParams';
import { hasData } from '@Utils/graphql';

import styles from './StaticPageContainer.module.scss';

const StaticPageContainer: React.FunctionComponent<{}> = () => {
  const { slug: slugName } = useRouteParams(RouteConfig.StaticPage);

  const { loading, data, error } = useGetStaticPageQuery({
    variables: { slug: slugName },
    fetchPolicy: 'network-only',
  });

  if (error || !hasData(data, loading, 'pageTranslation')) {
    return <Navigate to={DEFAULT_HOMEPAGE} replace />;
  }

  if (loading) {
    return (
      <Layout background={LayoutBackgrounds.grayBackground} noTopPadding>
        <Paper>
          <PaperBody>
            <Loading />
          </PaperBody>
        </Paper>
      </Layout>
    );
  }

  const slugText = filterXSS(data?.pageTranslation?.text || '');

  return (
    <DocumentTitle title={data?.pageTranslation.title}>
      <Layout background={LayoutBackgrounds.grayBackground} noTopPadding>
        <PageHeader>
          <Row column className={styles.titleAlignment}>
            <PageTitle msg={data?.pageTranslation?.title || ''} color="primary" />
          </Row>
        </PageHeader>

        <Container>
          <Paper width={PaperWidth.full} depth={PaperDepth.one}>
            <PaperBody>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: slugText }} className={styles.htmlTextDisplay} />
            </PaperBody>
          </Paper>
        </Container>
      </Layout>
    </DocumentTitle>
  );
};

export default StaticPageContainer;
